import { useEffect, useState } from "react";
import { useMutation, type DocumentNode } from "@apollo/client";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import { CREATE_BACKGROUND_CHECK_ORDER_MUTATION, SEND_BACKGROUND_CHECK_INVITATION_EMAIL_MUTATION } from "./mutations";
import { CREATE_ORDER_DIALOG_HEADLINE, SEND_INVITATION_DIALOG_HEADLINE } from "./backgroundCheckCopy";
import type { BackgroundCheckOrderAction, BackgroundCheckOrderActionPayload } from "./types";
import { showSnackbar } from "@/lib/snackbarUtils";

interface BackgroundCheckOrderActionsModalProps {
  action: BackgroundCheckOrderAction | null;
  open: boolean;
  onClose: () => void;
  personId: string;
  orderId?: string;
  refetchQueries?: DocumentNode[];
}

function getDialogCopy(action: BackgroundCheckOrderAction | null) {
  if (action === "email") {
    return {
      headline: SEND_INVITATION_DIALOG_HEADLINE,
      description:
        "An email will be sent with a link to access the HireRight screening portal for this order. If the previous invitation link has expired, a new link will automatically be generated.",
      confirmLabel: "Send Email",
      errorDescription: "The invitation email could not be sent."
    };
  }

  return {
    headline: CREATE_ORDER_DIALOG_HEADLINE,
    description: "Creates a new HireRight screening order and sends the prospect an invitation email. Continue?",
    confirmLabel: "Create Order",
    errorDescription: "The screening order could not be created."
  };
}

export default function BackgroundCheckOrderActionsModal({
  action,
  open,
  onClose,
  personId,
  orderId,
  refetchQueries = []
}: BackgroundCheckOrderActionsModalProps) {
  const [bannerError, setBannerError] = useState("");
  const copy = getDialogCopy(action);

  const [createOrder, { loading: creating }] = useMutation(CREATE_BACKGROUND_CHECK_ORDER_MUTATION, {
    refetchQueries,
    awaitRefetchQueries: true
  });

  const [sendInvitationEmail, { loading: emailing }] = useMutation(SEND_BACKGROUND_CHECK_INVITATION_EMAIL_MUTATION, {
    refetchQueries,
    awaitRefetchQueries: true
  });

  const loading = creating || emailing;

  useEffect(() => {
    if (!open) {
      setBannerError("");
    }
  }, [open]);

  const handleClose = () => {
    if (loading) return;
    setBannerError("");
    onClose();
  };

  async function submit() {
    if (!action) return;

    try {
      setBannerError("");

      let payload: BackgroundCheckOrderActionPayload | undefined;

      if (action === "create") {
        const response = await createOrder({ variables: { personId } });
        payload = response.data?.createBackgroundCheckOrder;
      } else {
        const response = await sendInvitationEmail({ variables: { orderId: orderId! } });
        payload = response.data?.sendBackgroundCheckInvitationEmail;
      }

      if (payload?.success) {
        showSnackbar(payload.message || (action === "create" ? "Screening order created" : "Invitation email sent"));
        handleClose();
        return;
      }

      setBannerError(payload?.message || copy.errorDescription);
    } catch (error) {
      const message = (error as Error).message || copy.errorDescription;
      setBannerError(`Error: ${message}`);
    }
  }

  return (
    <Dialog
      className="p-6 w-[480px]"
      open={open}
      onClosed={handleClose}
      headline={copy.headline}
      displayOverflow
      disableClose={loading}
      actions={
        <>
          <Button
            variant="text"
            onClick={handleClose}
            testId="background-check-order-actions-cancel-button"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            testId="background-check-order-actions-confirm-button"
            disabled={loading || !action || (action !== "create" && !orderId)}
          >
            {copy.confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {!!bannerError && <Banner inline variant="error" description={copy.errorDescription} subText={bannerError} />}

        <p className="text-body-medium text-sys-brand-on-surface-variant">{copy.description}</p>
      </div>
    </Dialog>
  );
}
